import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { stringifyQuery, parseQuery, useRoute, useRouter } from 'vue-router';
import { bitterAlert } from '@/plugins/BBitterAlert';

/**
 * @description: urlからパラメータを除去したい場合は、返却されたparam.valueにundefinedを代入してください
 */
const useQueryParameter = <T extends string | string[] | number | undefined>(key: string, transformQueryToValue: (query: string | string[]) => T = (query) => query as T) => {
  const router = useRouter();
  const route = useRoute();

  watch(() => route.query, () => {
    innerParam.value = transformQueryToValue(route.query[key]);
  });

  const innerParam = ref<T>(transformQueryToValue(route.query[key]));
  const param = computed({
    get: (): T => {
      return innerParam.value as T;
    },
    set: (value: T) => {
      innerParam.value = value;
      const params = new URLSearchParams(window.location.search);
      const queryString = stringifyQuery({ ...parseQuery(params.toString()), [key]: value });
      router.options.history.push(`${route.path}${queryString ? `?${queryString}` : ''}`);
    },
  });

  const getAndValidateParamAsInteger = (errorMessage?: string, errorTitle?: string): number | undefined => {
    const i18n = useI18n();

    if (!(key in route.query)) {
      return;
    }
    const queryValue = Number(route.query[key]);
    if (!queryValue || isNaN(queryValue) || !Number.isInteger(queryValue)) {
      bitterAlert.show({
        title: errorTitle || i18n.t('general.error'),
        text: errorMessage || i18n.t('general.error'),
        buttonsCancel: false,
      });
      return;
    }
    return queryValue;
  };

  return {
    param,
    getAndValidateParamAsInteger,
  };
};

export {
  useQueryParameter,
};
